import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';

import type { ListConfigsFilters } from '@/api/client';

export interface ConfigToolbarOptions {
  readonly namespaces: readonly string[];
  readonly tags: readonly string[];
  readonly scopes: readonly string[];
}

interface ConfigToolbarProps {
  readonly filters: ListConfigsFilters;
  readonly options: ConfigToolbarOptions;
  readonly onChange: (next: Partial<ListConfigsFilters>) => void;
}

/**
 * Search + facet bar over the config list. Search (`q`) is server-side; the
 * namespace/tag/scope facets and the "Only changed" (`diffOnly`) toggle are
 * passed straight through to the list query. The facet option lists are
 * populated from the catalog so they cover every configurable key, not just the
 * currently visible rows.
 */
export function ConfigToolbar({ filters, options, onChange }: ConfigToolbarProps): ReactElement {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="min-w-56 flex-1">
        <label htmlFor="config-search" className="mb-1 block text-xs text-muted-foreground">
          {t('routes.admin.configs.toolbar.searchLabel')}
        </label>
        <Input
          id="config-search"
          type="search"
          value={filters.q ?? ''}
          onChange={(e) => {
            onChange({ q: e.target.value });
          }}
          placeholder={t('routes.admin.configs.toolbar.searchPlaceholder')}
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      <FacetSelect
        id="config-namespace"
        label={t('routes.admin.configs.toolbar.namespaceLabel')}
        allLabel={t('routes.admin.configs.toolbar.allNamespaces')}
        value={filters.namespace ?? ''}
        values={options.namespaces}
        onChange={(namespace) => {
          onChange({ namespace });
        }}
      />

      <FacetSelect
        id="config-tag"
        label={t('routes.admin.configs.toolbar.tagLabel')}
        allLabel={t('routes.admin.configs.toolbar.allTags')}
        value={filters.tag ?? ''}
        values={options.tags}
        onChange={(tag) => {
          onChange({ tag });
        }}
      />

      <FacetSelect
        id="config-scope"
        label={t('routes.admin.configs.toolbar.scopeLabel')}
        allLabel={t('routes.admin.configs.toolbar.allScopes')}
        value={filters.scope ?? ''}
        values={options.scopes}
        onChange={(scope) => {
          onChange({ scope });
        }}
      />

      <label className="flex h-9 items-center gap-2 text-sm" htmlFor="config-diff-only">
        <input
          id="config-diff-only"
          type="checkbox"
          className="h-4 w-4 rounded border-border"
          checked={filters.diffOnly ?? false}
          onChange={(e) => {
            onChange({ diffOnly: e.target.checked });
          }}
        />
        {t('routes.admin.configs.toolbar.onlyChanged')}
      </label>
    </div>
  );
}

interface FacetSelectProps {
  readonly id: string;
  readonly label: string;
  readonly allLabel: string;
  readonly value: string;
  readonly values: readonly string[];
  readonly onChange: (next: string) => void;
}

function FacetSelect({
  id,
  label,
  allLabel,
  value,
  values,
  onChange,
}: FacetSelectProps): ReactElement {
  return (
    <div className="min-w-40">
      <label htmlFor={id} className="mb-1 block text-xs text-muted-foreground">
        {label}
      </label>
      <Select
        id={id}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      >
        <option value="">{allLabel}</option>
        {values.map((v) => (
          <option key={v} value={v}>
            {v}
          </option>
        ))}
      </Select>
    </div>
  );
}
